import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import { routes } from './routes'

Vue.use(VueRouter)

// Inject branchname/coname/cocode from the auth session into every route's
// meta so the shellRoute props function can pass them down without touching
// the legacy components.
function hydrateMeta(to) {
  const g = store.getters
  to.meta.branchname = g['auth/branchname']
  to.meta.coname     = g['auth/coname']
  to.meta.cocode     = g['auth/cocode']
  to.meta.loc        = g['auth/loc']
  to.meta.abr        = g['auth/abr']
}

const router = new VueRouter({
  mode: 'history',
  base: import.meta.env.VITE_BASE_PATH || '/',
  routes,
  scrollBehavior(_to, _from, saved) {
    return saved || { x: 0, y: 0 }
  },
})

router.beforeEach((to, from, next) => {
  const isAuth = store.getters['auth/isAuthenticated']

  // On cold app start (from.name === null = initial navigation), send
  // authenticated admin users straight to the rental dashboard instead of home.
  if (to.name === 'home' && isAuth && from.name === null) {
    return next({ name: 'rental-dashboard' })
  }

  if (to.meta.guestOnly && isAuth) return next({ name: 'rental-dashboard' })
  if (to.meta.requiresAuth && !isAuth) {
    return next({ name: 'login', query: { redirect: to.fullPath } })
  }
  // Admin-role pages (role_user.role_id 1); everyone else lands on rental.
  if (to.meta.adminOnly && String(store.state.auth.role) !== '1') {
    return next({ name: 'rental-dashboard' })
  }
  hydrateMeta(to)
  next()
})

// After a deploy, a tab still running the old build asks for page chunks
// (assets/<page>-<oldhash>.js) that no longer exist, and the import fails
// ("Expected a JavaScript module script but the server responded with a MIME
// type of text/html"). Reload once onto the new build — to the page the user
// was opening — instead of leaving them on a dead screen. The sessionStorage
// guard stops a reload loop if the chunk is genuinely broken.
const RELOAD_KEY = 'chunk-reload-at'
function isChunkLoadError(err) {
  const msg = String((err && err.message) || err || '')
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Expected a JavaScript.*module script|MIME type/i.test(msg)
}
function reloadForNewBuild(path) {
  let last = 0
  try { last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0 } catch (e) { /* storage blocked */ }
  if (Date.now() - last < 10000) return false
  try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())) } catch (e) { /* storage blocked */ }
  window.location.assign(path || window.location.href)
  return true
}
// vue-router 3's onError only gets the error, so remember where we were going.
let pendingPath = null
router.beforeEach((to, _from, next) => {
  pendingPath = to.fullPath
  next()
})
router.onError((err) => {
  if (isChunkLoadError(err)) {
    reloadForNewBuild(pendingPath ? router.resolve(pendingPath).href : null)
  }
})
// Vite's own signal for a failed preload of a lazy chunk.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadForNewBuild()) event.preventDefault()
})

export default router
